import { useEffect, useId, useRef } from "react";
import videojs from "video.js";
import type Player from "video.js/dist/types/player";
import {
  MUX_IMAGE,
  MUX_STREAM,
} from "shared/components/molecules/VideoPlayer/VideoPlayer";

type MutedMuxPreviewProps = {
  playbackId: string;
  /** When false, the player is disposed (e.g. hover ended). Defaults to true. */
  active?: boolean;
  className?: string;
};

/**
 * Muted looping HLS preview (Video.js), always filling its parent box.
 * Uses pointer-events: none so parent buttons/cards remain the hit target
 * (important on iOS Safari).
 */
export const MutedMuxPreview = ({
  playbackId,
  active = true,
  className,
}: MutedMuxPreviewProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const scopeId = useId().replace(/:/g, "");

  useEffect(() => {
    if (!active) {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
      return;
    }

    if (playerRef.current) return;
    const container = containerRef.current;
    if (!container) return;

    const videoEl = document.createElement("video");
    videoEl.className = "video-js";
    videoEl.setAttribute("playsinline", "true");
    videoEl.setAttribute("webkit-playsinline", "true");
    container.appendChild(videoEl);

    const player = videojs(videoEl, {
      controls: false,
      autoplay: true,
      muted: true,
      loop: true,
      preload: "auto",
      // Fill the parent — do not use fluid (fluid expands to video aspect ratio).
      fill: true,
      fluid: false,
      poster: `${MUX_IMAGE}/${playbackId}/thumbnail.webp`,
      sources: [
        {
          src: `${MUX_STREAM}/${playbackId}.m3u8`,
          type: "application/x-mpegURL",
        },
      ],
    });
    player.ready(() => {
      player.fill(true);
      void player.play()?.catch(() => {
        // Autoplay can be blocked; poster remains visible.
      });
    });
    playerRef.current = player;

    return () => {
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [playbackId, active]);

  if (!active) return null;

  return (
    <div
      aria-hidden
      data-mux-preview={scopeId}
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      <div
        ref={containerRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      />
      <style>{`
        [data-mux-preview="${scopeId}"] .video-js {
          position: absolute !important;
          inset: 0 !important;
          width: 100% !important;
          height: 100% !important;
          padding-top: 0 !important;
          pointer-events: none !important;
        }
        [data-mux-preview="${scopeId}"] .video-js .vjs-tech {
          object-fit: cover !important;
          width: 100% !important;
          height: 100% !important;
          pointer-events: none !important;
        }
        [data-mux-preview="${scopeId}"] .video-js .vjs-control-bar,
        [data-mux-preview="${scopeId}"] .video-js .vjs-big-play-button {
          display: none !important;
        }
      `}</style>
    </div>
  );
};
